import { useEffect, useRef, useState } from 'react';
import type { CapturedPhoto, ShotContext } from './types';
import { CornerBrackets } from './CornerBrackets';
import { saveBlob } from '../lib/blobStore';
import { addDescription, type Taxonomy } from '../lib/taxonomy';

interface Props {
  context: ShotContext;
  taxonomy: Taxonomy;
  photoCount: number;
  notepadCount: number;
  showWrapUp: boolean;
  onShutter: (photo: CapturedPhoto) => void;
  onWrapUp: () => void;
  onOpenContext: () => void;
  onContextChange: (next: ShotContext) => void;
  onTaxonomyMutated: (next: Taxonomy) => void;
  onOpenNotepad: () => void;
  onOpenGallery: () => void;
}

function splitDescription(s: string): string[] {
  return s.split(', ').map((x) => x.trim()).filter(Boolean);
}

export function Viewfinder({
  context,
  taxonomy,
  photoCount,
  notepadCount,
  showWrapUp,
  onShutter,
  onWrapUp,
  onOpenContext,
  onContextChange,
  onTaxonomyMutated,
  onOpenNotepad,
  onOpenGallery,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const pinchRef = useRef<{ startDist: number; startZoom: number } | null>(null);
  const swipeRef = useRef<{ x: number; y: number; triggered: boolean } | null>(null);
  const [wrapUpOpen, setWrapUpOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [isMock, setIsMock] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addText, setAddText] = useState('');
  const [savingAdd, setSavingAdd] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [zoomRange, setZoomRange] = useState<{
    min: number;
    max: number;
    step: number;
    native: boolean;
  }>({ min: 1, max: 4, step: 0.1, native: false });

  const selected = splitDescription(context.description);
  const cat = taxonomy.categories.find((c) => c.name === context.category);
  const lab = cat?.labels.find((l) => l.name === context.label);
  const available = lab?.descriptions ?? [];

  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;
    let mockInterval: number | null = null;

    const requestedMock =
      new URLSearchParams(window.location.search).get('mock') === '1';

    function startMockStream(): MediaStream {
      const c = document.createElement('canvas');
      c.width = 1280;
      c.height = 720;
      const cx = c.getContext('2d');
      if (!cx) throw new Error('canvas 2d context unavailable for mock');
      let frame = 0;
      const palette = ['#1e3a8a', '#0f766e', '#7c2d12', '#581c87', '#7f1d1d'];
      const draw = () => {
        const i = Math.floor(frame / 30) % palette.length;
        const grad = cx.createLinearGradient(0, 0, c.width, c.height);
        grad.addColorStop(0, palette[i]);
        grad.addColorStop(1, palette[(i + 1) % palette.length]);
        cx.fillStyle = grad;
        cx.fillRect(0, 0, c.width, c.height);
        cx.fillStyle = 'rgba(255,255,255,0.85)';
        cx.font = 'bold 80px system-ui';
        cx.textAlign = 'center';
        cx.fillText('MOCK CAMERA', c.width / 2, c.height / 2 - 20);
        cx.font = '32px system-ui';
        cx.fillText(
          new Date().toISOString().slice(0, 19).replace('T', ' '),
          c.width / 2,
          c.height / 2 + 40,
        );
        cx.fillText(`frame ${frame}`, c.width / 2, c.height / 2 + 90);
        frame++;
      };
      draw();
      mockInterval = window.setInterval(draw, 100);
      return c.captureStream(10);
    }

    async function start() {
      if (requestedMock) {
        try {
          stream = startMockStream();
          setIsMock(true);
        } catch (e) {
          if (!cancelled) setError(`mock camera failed: ${(e as Error).message}`);
          return;
        }
      } else {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' } },
            audio: false,
          });
        } catch {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: false,
            });
          } catch {
            try {
              stream = startMockStream();
              setIsMock(true);
            } catch (e) {
              if (!cancelled) setError(`camera unavailable: ${(e as Error).message}`);
              return;
            }
          }
        }
      }
      if (cancelled) {
        stream?.getTracks().forEach((t) => t.stop());
        if (mockInterval !== null) window.clearInterval(mockInterval);
        return;
      }
      if (videoRef.current && stream) {
        videoRef.current.srcObject = stream;
        const markReady = () => setVideoReady(true);
        if (videoRef.current.readyState >= 2) {
          markReady();
        } else {
          videoRef.current.addEventListener('loadedmetadata', markReady, {
            once: true,
          });
        }
        try {
          await videoRef.current.play();
        } catch {
          // play() can reject if user navigates away mid-load; ignore
        }
        const track = stream.getVideoTracks()[0];
        trackRef.current = track ?? null;
        if (track) {
          const caps = (track.getCapabilities?.() as unknown as {
            zoom?: { min?: number; max?: number; step?: number };
          }) ?? {};
          if (caps.zoom && typeof caps.zoom.max === 'number' && caps.zoom.max > 1) {
            setZoomRange({
              min: caps.zoom.min ?? 1,
              max: caps.zoom.max,
              step: caps.zoom.step ?? 0.1,
              native: true,
            });
          } else {
            setZoomRange({ min: 1, max: 4, step: 0.1, native: false });
          }
        }
      }
    }

    void start();
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((t) => t.stop());
      if (mockInterval !== null) window.clearInterval(mockInterval);
    };
  }, []);

  async function applyZoom(next: number) {
    const clamped = Math.max(zoomRange.min, Math.min(zoomRange.max, next));
    setZoom(clamped);
    if (zoomRange.native && trackRef.current) {
      try {
        await trackRef.current.applyConstraints({
          advanced: [{ zoom: clamped } as unknown as MediaTrackConstraintSet],
        });
      } catch {
        // If native zoom errors, keep visual scale as a backstop.
        setZoomRange((r) => ({ ...r, native: false }));
      }
    }
  }

  function distance(a: React.Touch, b: React.Touch): number {
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }

  function onTouchStart(e: React.TouchEvent) {
    if (e.touches.length === 2) {
      pinchRef.current = {
        startDist: distance(e.touches[0], e.touches[1]),
        startZoom: zoom,
      };
    }
  }
  function onTouchMove(e: React.TouchEvent) {
    if (e.touches.length === 2 && pinchRef.current) {
      const d = distance(e.touches[0], e.touches[1]);
      const ratio = d / pinchRef.current.startDist;
      void applyZoom(pinchRef.current.startZoom * ratio);
    }
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (e.touches.length < 2) pinchRef.current = null;
  }

  function onChipTouchStart(e: React.TouchEvent) {
    if (e.touches.length !== 1) return;
    swipeRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      triggered: false,
    };
  }
  function onChipTouchMove(e: React.TouchEvent) {
    if (e.touches.length !== 1 || !swipeRef.current || swipeRef.current.triggered) return;
    const dx = e.touches[0].clientX - swipeRef.current.x;
    const dy = e.touches[0].clientY - swipeRef.current.y;
    if (dy < -30 && Math.abs(dy) > Math.abs(dx) * 1.5) {
      swipeRef.current.triggered = true;
      onOpenContext();
    }
  }
  function onChipTouchEnd() {
    swipeRef.current = null;
  }

  async function handleShutter() {
    if (capturing || error) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    if (video.videoWidth === 0 || video.videoHeight === 0) return;
    setCapturing(true);
    const capturedAt = new Date().toISOString();
    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas 2d context unavailable');
      if (!zoomRange.native && zoom > 1.01) {
        // CSS-zoom path: crop source region to match what the inspector sees.
        const srcW = video.videoWidth / zoom;
        const srcH = video.videoHeight / zoom;
        const srcX = (video.videoWidth - srcW) / 2;
        const srcY = (video.videoHeight - srcH) / 2;
        ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);
      } else {
        // Native zoom (or no zoom): video already produces zoomed pixels.
        ctx.drawImage(video, 0, 0);
      }
      const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
          'image/jpeg',
          0.9,
        );
      });
      const buffer = await blob.arrayBuffer();
      const hashBuf = await crypto.subtle.digest('SHA-256', buffer);
      const checksum = Array.from(new Uint8Array(hashBuf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      const photoId = crypto.randomUUID();
      const blobKey = `${photoId}.jpg`;
      await saveBlob(blobKey, blob);
      onShutter({
        id: photoId,
        blobKey,
        checksum,
        capturedAt,
        context,
        flagged: false,
        noteText: '',
      });
    } finally {
      setCapturing(false);
    }
  }

  function toggleDescription(d: string) {
    if (context.locked) return;
    const next = selected.includes(d)
      ? selected.filter((x) => x !== d)
      : [...selected, d];
    onContextChange({ ...context, description: next.join(', ') });
  }

  async function submitInlineAdd() {
    const v = addText.trim();
    if (!v || savingAdd || context.locked) return;
    setSavingAdd(true);
    try {
      const next = await addDescription(context.category, context.label, v);
      onTaxonomyMutated(next);
      onContextChange({
        ...context,
        description: [...selected.filter((x) => x !== v), v].join(', '),
      });
      setAddText('');
      setAdding(false);
    } finally {
      setSavingAdd(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black text-white [touch-action:none]"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        style={
          !zoomRange.native && zoom > 1.01
            ? { transform: `scale(${zoom})`, transformOrigin: 'center' }
            : undefined
        }
        playsInline
        muted
      />
      <canvas ref={canvasRef} className="hidden" />
      <CornerBrackets color={context.bracketColor} />

      {isMock && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full bg-purple-600 px-3 py-1 text-xs font-bold uppercase tracking-wider">
          Mock camera
        </div>
      )}

      <button
        onClick={onOpenGallery}
        disabled={photoCount === 0}
        className="absolute top-4 left-16 rounded-full bg-black/60 px-3 py-1 text-sm active:bg-black/80 disabled:opacity-60"
      >
        {photoCount} {photoCount === 1 ? 'photo' : 'photos'}
        {photoCount > 0 && <span className="ml-2 opacity-70">›</span>}
      </button>

      {showWrapUp && (
        <button
          onClick={() => setWrapUpOpen(true)}
          className="absolute top-4 right-16 rounded-full bg-eva-teal px-4 py-1.5 text-sm font-semibold"
        >
          Wrap Up
        </button>
      )}

      {wrapUpOpen && (
        <div
          className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setWrapUpOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl bg-white p-4 text-black"
          >
            <h2 className="text-lg font-bold">Done shooting?</h2>
            <p className="mt-1 text-sm text-gray-600">
              You've captured {photoCount} photo{photoCount === 1 ? '' : 's'} this session.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setWrapUpOpen(false)}
                className="flex-1 rounded-lg border border-gray-300 bg-white py-2 text-sm font-semibold"
              >
                Keep Shooting
              </button>
              <button
                onClick={() => {
                  setWrapUpOpen(false);
                  onWrapUp();
                }}
                className="flex-1 rounded-lg bg-eva-teal py-2 text-sm font-semibold text-white"
              >
                Review →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header: category · label + full-menu link */}
      <div className="absolute bottom-48 left-0 right-0 flex flex-col items-center gap-1 px-4">
        <div className="flex items-center gap-3 rounded-full bg-black/60 px-4 py-1 text-xs">
          <span className="uppercase tracking-wide opacity-80">
            {context.category} · {context.label}
          </span>
          {!context.locked && (
            <button
              onClick={onOpenContext}
              className="underline opacity-70"
            >
              full menu
            </button>
          )}
          {context.locked && (
            <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] opacity-70">
              locked
            </span>
          )}
        </div>
        {!context.locked && (
          <div className="h-1 w-10 rounded-full bg-white/40" aria-hidden />
        )}
      </div>

      {/* Description chip row */}
      {context.locked ? (
        <div className="absolute bottom-32 left-0 right-0 flex flex-wrap items-center justify-center gap-2 px-4">
          {selected.map((d) => (
            <span
              key={d}
              className="rounded-full bg-bracket-blue/30 px-3 py-1 text-sm"
            >
              {d}
            </span>
          ))}
        </div>
      ) : (
        <div
          className="absolute bottom-32 left-0 right-0 overflow-x-auto"
          onTouchStart={onChipTouchStart}
          onTouchMove={onChipTouchMove}
          onTouchEnd={onChipTouchEnd}
        >
          <div className="flex items-center gap-2 px-4 pb-1">
            {available.map((d) => {
              const isSelected = selected.includes(d);
              return (
                <button
                  key={d}
                  onClick={() => toggleDescription(d)}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-sm ${
                    isSelected
                      ? 'border-bracket-blue bg-bracket-blue text-white'
                      : 'border-white/40 bg-black/50 text-white/85'
                  }`}
                >
                  {d}
                </button>
              );
            })}
            {adding ? (
              <div className="flex shrink-0 items-center gap-1">
                <input
                  autoFocus
                  value={addText}
                  onChange={(e) => setAddText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void submitInlineAdd();
                    if (e.key === 'Escape') {
                      setAdding(false);
                      setAddText('');
                    }
                  }}
                  placeholder="new description"
                  disabled={savingAdd}
                  className="w-40 rounded-full border border-white/50 bg-black/60 px-3 py-1.5 text-sm text-white outline-none"
                />
                <button
                  onClick={() => void submitInlineAdd()}
                  disabled={savingAdd || !addText.trim()}
                  className="rounded-full bg-eva-teal px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
                >
                  {savingAdd ? '…' : 'Add'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setAdding(true)}
                className="shrink-0 rounded-full border border-dashed border-white/50 px-3 py-1.5 text-sm text-white/85"
              >
                + new
              </button>
            )}
          </div>
        </div>
      )}

      {/* Zoom +/- stack (left edge) */}
      {!context.locked && (
        <div className="absolute bottom-8 left-4 flex flex-col items-center gap-2">
          <button
            onClick={() => void applyZoom(zoom + (zoomRange.step || 0.1) * 5)}
            disabled={zoom >= zoomRange.max - 0.01}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-xl leading-none disabled:opacity-30 [touch-action:manipulation]"
            aria-label="Zoom in"
          >
            +
          </button>
          {zoom > 1.01 && (
            <div className="rounded-full bg-black/60 px-2 py-0.5 text-xs font-semibold tabular-nums">
              {zoom.toFixed(1)}x
            </div>
          )}
          <button
            onClick={() => void applyZoom(zoom - (zoomRange.step || 0.1) * 5)}
            disabled={zoom <= zoomRange.min + 0.01}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-xl leading-none disabled:opacity-30 [touch-action:manipulation]"
            aria-label="Zoom out"
          >
            −
          </button>
        </div>
      )}

      <button
        onClick={onOpenNotepad}
        className="absolute bottom-8 right-8 flex h-14 w-14 items-center justify-center rounded-full bg-black/60"
        aria-label="Open notepad"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
        {notepadCount > 0 && (
          <span className="absolute -top-1 -right-1 rounded-full bg-bracket-amber px-1.5 py-0.5 text-xs font-bold text-black">
            {notepadCount}
          </span>
        )}
      </button>

      <button
        onClick={handleShutter}
        disabled={capturing || !!error || !videoReady}
        className="absolute bottom-8 left-1/2 h-20 w-20 -translate-x-1/2 rounded-full border-4 border-white bg-white/20 active:bg-white/40 disabled:opacity-50 [touch-action:manipulation]"
        aria-label="Capture photo"
      >
        <span className="block h-full w-full rounded-full border-2 border-black/40" />
      </button>
      {!videoReady && !error && (
        <div className="absolute bottom-32 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-3 py-1 text-xs opacity-80">
          starting camera…
        </div>
      )}

      {error && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg bg-red-600 px-4 py-2 text-sm">
          {error}
        </div>
      )}
    </div>
  );
}
